<template>
	<div>
		<div class="relative flex">
			<div ref="wrapperRef" class="isolate relative flex w-full">
				<ul ref="listRef" :class="listClass">
					<slot v-if="$slots.firstItem" name="firstItem" />
					<slot
						v-for="(item, index) in items"
						v-bind="{item, index}"
						name="item"
					/>
				</ul>
			</div>
			<slot
				v-if="isSliderActive"
				v-bind="slotProps"
				name="inner"
			/>
		</div>
		<div v-if="isSliderActive && ($slots.footer)" class="relative flex">
			<slot v-bind="slotProps" name="footer" />
		</div>
	</div>
</template>

<script lang="ts" setup generic="T">
	import {throttle} from 'lodash-es';
	import {gsap} from 'gsap';
	import {Draggable} from 'gsap/Draggable';
	import {InertiaPlugin} from 'gsap/InertiaPlugin';
	import type {VNode} from 'vue';
	import {findClosestNumber} from '~/utils/findClosestNumber';

	type Props = {
		items: T[];
		pagePerItem?: boolean;
		listClass?: string;
		snapItems?: boolean;
		centerItemSnap?: boolean;
		infinite?: boolean;
		autoplay?: boolean | number;
		alwaysActive?: boolean;
		calculateListWidthFromItemCount?: boolean;
		pauseAutoplay?: boolean;
		hasFirstItem?: boolean;
	};

	type Emits = {
		(e: 'indexUpdate', index: number): void;
		(e: 'autoplay', index: number): void;
		(e: 'drag', index: number): void;
	};

	type ScrollControls = {
		increment: () => void;
		decrement: () => void;
		seek: (index: number, stopAutoplay?: boolean) => void;
		kill: () => void;
	};

	type SlotProps = {
		current: number;
		total: number;
	} & ScrollControls;

	const props = withDefaults(defineProps<Props>(), {
		listClass: 'inline-flex w-full min-w-max mb-6',
		infinite: false,
		autoplay: false,
		pauseAutoplay: false,
	});
	const emit = defineEmits<Emits>();

	defineSlots<{
		item(props: {item: T; index: number}): VNode[]
		firstItem(): VNode[]
		inner(props: SlotProps): VNode[]
		footer(props: SlotProps): VNode[]
	}>();

	defineExpose({
		setupDraggable,
		seek: (index: number, stopAutoplay = true) => scrollControls.value.seek(index, stopAutoplay),
	});

	const wrapperRef = ref<HTMLElement | null>(null);
	const listRef = ref<HTMLElement | null>(null);
	const {width: realListWidth} = useElementSize(listRef);
	const {width: wrapperWidth} = useElementSize(wrapperRef);
	const sliderIndex = ref<number>(0);
	const listItemWidth = ref<number>(0);

	const preferredMotion = usePreferredReducedMotion();
	const isVisible = useElementVisibility(wrapperRef);
	const noop = () => {};

	const scrollControls: Ref<ScrollControls> = ref({
		increment: noop,
		decrement: noop,
		kill: noop,
		seek: noop,
	});

	const listWidth = computed<number>(() => (
		props.calculateListWidthFromItemCount
			? listItemWidth.value * props.items.length
			: realListWidth.value
	));

	const pagesTotal = computed<number>(() => {
		if (!wrapperWidth.value || !listWidth.value) {
			return 0;
		}

		return props.pagePerItem
			? props.items.length + (props.hasFirstItem ? 1 : 0)
			: Math.round(listWidth.value / wrapperWidth.value);
	});

	const slotProps = computed<SlotProps>(() => ({
		...scrollControls.value,
		current: sliderIndex.value + 1,
		total: pagesTotal.value,
	}));

	const isSliderActive = computed<boolean>(() => props.alwaysActive || listWidth.value > wrapperWidth.value);
	const isFirst = computed<boolean>(() => sliderIndex.value <= 0);
	const isLast = computed<boolean>(() => sliderIndex.value >= pagesTotal.value - 1);

	const autoplay = props.autoplay && props.infinite
		? useIntervalFn(
			() => {
				scrollControls.value.seek(sliderIndex.value + 1, false);
				emit('autoplay', sliderIndex.value);
			},
			typeof props.autoplay === 'number'
				? props.autoplay
				: 2500,
		)
		: null;

	watch(sliderIndex, () => emit('indexUpdate', sliderIndex.value));

	watch([wrapperWidth, listWidth, isSliderActive], setupDraggable);

	watch(isVisible, (newVal) => {
		if (newVal && !props.pauseAutoplay) {
			autoplay?.resume();
		} else {
			autoplay?.pause();
		}
	});

	watch(() => props.pauseAutoplay, (shouldPause) => {
		if (shouldPause) {
			autoplay?.pause();
		} else if (isVisible.value) {
			autoplay?.resume();
		}
	});

	onMounted(() => {
		gsap.registerPlugin(Draggable, InertiaPlugin);
	});

	onBeforeUnmount(() => {
		scrollControls?.value.kill();
	});

	function recalculateListItemWidth(): void {
		if (!listRef.value?.children?.length) {
			return;
		}

		const [firstItem, secondItem] = listRef.value.children;
		const listStyle = window.getComputedStyle(listRef.value);
		const itemStyle = window.getComputedStyle(secondItem ?? firstItem);

		listItemWidth.value = (firstItem as HTMLElement).offsetWidth
			+ (parseInt(listStyle.gap) || 0)
			+ (parseInt(itemStyle.marginLeft) || 0)
			+ (parseInt(itemStyle.marginRight) || 0);
	}

	async function setupDraggable(): Promise<void> {
		if (!wrapperRef.value || !listRef.value || !isSliderActive.value) {
			scrollControls.value.kill();
			return;
		}

		recalculateListItemWidth();

		await nextTick();

		scrollControls.value.kill();
		scrollControls.value = props.infinite
			? setupInfiniteScroll()
			: setupBoundedScroll();
	}

	function setupInfiniteScroll(): ScrollControls {
		const animations = Array
			.from(listRef.value?.children ?? [])
			.map((item, index) => {
				const limitFrom = listItemWidth.value * (-index - 1);
				const limitTo = limitFrom + listWidth.value;

				return gsap
					.timeline({repeat: -1, paused: true})
					.fromTo(
						item,
						{x: listWidth.value * 2},
						{
							x: -listWidth.value,
							ease: 'none',
							duration: 3,
							modifiers: {x: (x) => `${gsap.utils.wrap(limitFrom, limitTo, parseFloat(x))}px`},
						},
					)
					.time(1);
			});

		function updateProgress(this: Draggable): void {
			const progress = gsap.utils.wrap(0, listWidth.value, -this.x / 3) / listWidth.value;
			animations.forEach((a) => a.progress(progress));
		}

		function seek(index: number, stopAutoplay = true): void {
			const normalizedTime = gsap.utils.wrap(1, 2, animations[0].time());
			const finalTime = 1 + index / pagesTotal.value;

			sliderIndex.value = mod(index, pagesTotal.value);
			animations.forEach((anim) => {
				anim
					.time(normalizedTime, true)
					.tweenTo(finalTime, {
						duration: preferredMotion.value === 'reduce' ? 0 : 0.4,
						ease: 'power1.inOut',
					});
			});

			gsap.set(draggableInstance.target, {x: -getOffsetFromIndex(sliderIndex.value)});
			draggableInstance.update();

			if (stopAutoplay) {
				autoplay?.pause();
			}
		}

		const proxy = document.createElement('div');

		const draggableInstance = new Draggable(proxy, {
			type: 'x',
			trigger: wrapperRef.value,
			throwProps: true,
			throwResistance: 6000,
			zIndexBoost: false,
			onDragStart() {
				autoplay?.pause();
			},
			onDrag: updateProgress,
			onThrowUpdate: updateProgress,
			onRelease(): void {
				if (!isFinite(this.endX)) {
					return;
				}

				sliderIndex.value = mod(Math.round(-this.endX / listItemWidth.value), pagesTotal.value);
				emit('drag', sliderIndex.value);
			},
			snap: props.snapItems
				? {x: (x) => roundToMultiplesOf(x, listItemWidth.value)}
				: undefined,
		});

		return {
			seek: throttle(seek, 400),
			increment: throttle(() => seek(sliderIndex.value + 1), 400),
			decrement: throttle(() => seek(sliderIndex.value - 1), 400),
			kill() {
				listRef.value?.removeAttribute('style');
				draggableInstance.kill();
			},
		};
	}

	function setupBoundedScroll(): ScrollControls {
		function seek(index: number): void {
			const x = -getOffsetFromIndex(index);

			sliderIndex.value = index;

			if (!listRef.value) {
				return;
			}

			if (props.infinite) {
				gsap.set(listRef.value, {x});
				return;
			}
			gsap.to(listRef.value, {duration: 0.4, x});
		}

		const draggableInstance = new Draggable(listRef.value, {
			bounds: listRef.value!.parentElement,
			type: 'x',
			inertia: true,
			throwResistance: 6000,
			edgeResistance: 0.5,
			zIndexBoost: false,
			snap: props.snapItems
				? {
					x: (x) => {
						const offsets = Array.from({length: pagesTotal.value}, (_, i) => -getOffsetFromIndex(i));
						return findClosestNumber(x, offsets);
					},
				}
				: undefined,
			onDragEnd(this: Draggable) {
				sliderIndex.value = props.pagePerItem
					? Math.min(Math.abs(Math.round(this.endX / listItemWidth.value)), pagesTotal.value - 1)
					: Math.ceil(Math.abs(this.endX / wrapperRef.value!.offsetWidth));
			},
		});

		return {
			seek,
			increment() {
				if (!isLast.value) {
					seek(sliderIndex.value + 1);
				}
			},
			decrement() {
				if (!isFirst.value) {
					seek(sliderIndex.value - 1);
				}
			},
			kill() {
				listRef.value?.removeAttribute('style');
				draggableInstance.kill();
			},
		};
	}

	function getOffsetFromIndex(index: number): number {
		if (!props.pagePerItem) {
			return (wrapperRef.value?.offsetWidth ?? 0) * index;
		}

		const centerOffset = props.centerItemSnap
			? (wrapperRef.value!.offsetWidth - listItemWidth.value) / 2
			: 0;

		return listItemWidth.value * index - centerOffset;
	}
</script>
